
import Vue from 'vue';
import _isEmpty from 'lodash/isEmpty';
import { ROUTER } from '@/router/constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import { db, functions, FieldValue } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	Company,
	CompanyUserRoleDef,
	DbTimestamp,
	SCIM_SYNC_STATUS,
	ScimSyncStatus,
	Team,
	TeamUserRoleDef,
	normalizeSsoExceptionEntry,
} from '@run-diffusion/shared';
import moment from 'moment';
import pluralize from 'pluralize';
import EnterpriseUpsellBadge from '@/components/EnterpriseUpsellWrapper/EnterpriseUpsellBadge.vue';
import BackButton from '@/components/base/BackButton.vue';
import BaseBreadcrumbs from '@/components/base/BaseBreadcrumbs.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import EditCompanySsoConfigurations from '@/views/Team/TeamDashboard/EditCompany/EditCompanySsoConfigurations.vue';
import ProvisioningControlBanner from './ScimProvisioning/ProvisioningControlBanner.vue';
import ProvisioningStepCard from './ScimProvisioning/ProvisioningStepCard.vue';
import ScimConnectStep from './ScimProvisioning/ScimConnectStep.vue';
import ScimPlanStep from './ScimProvisioning/ScimPlanStep.vue';
import ScimDiffDialog from './ScimProvisioning/ScimDiffDialog.vue';
import { TeamUserMixin } from '@/mixins/TeamUserMixin';
import { initiateFileDownload } from '@/utils/file-io';
import {
	ScimAssignmentSnapshot,
	ScimGroupRow,
	TeamMappingDraft,
	assignmentDiffersFromSnapshot,
	companyRoleEmailsWithoutTeamMapping,
	getMemberEmailsList,
	getRowAssignmentType,
	getScimGroupAssignmentSnapshot,
	isRowUnmapped,
	sortTeamsByName,
	teamMappingsFromSnapshot,
	teamRoleMappingsForRow,
	teamsWithNoGroupInPlan,
} from './ScimProvisioning/scimPlan';
import {
	DiffRow,
	SyncResultPayload,
	buildSyncResultCsv,
	countByKind,
	flattenSyncResult,
} from './ScimProvisioning/scimDiff';

/**
 * Cap on the active-teams query. Reaching it is treated the same as a failed load: the page can no
 * longer promise it is showing every team a deploy would empty.
 */
const ACTIVE_TEAMS_QUERY_LIMIT = 500;

type StageTone = 'complete' | 'active' | 'idle' | 'blocked';
type StageState = { status: string; tone: StageTone; subtitle?: string };

/** A dry run is only quotable while the plan and the pushed groups it ran against are unchanged. */
type DryRunRecord = {
	at: number;
	payload: SyncResultPayload;
	rows: DiffRow[];
	scimSignature: string;
	planSignature: string;
	success: boolean;
	/**
	 * A deploy against this dry run failed part-way. The record is kept so the admin can retry without
	 * being sent back to step 03, but some of its changes may already have been written, so its counts
	 * are no longer a promise and every surface that quotes them has to say so.
	 */
	deployFailed?: boolean;
};

export default Vue.extend({
	name: 'CompanySettingsScimPage',
	metaInfo () {
		return {
			title: `SSO and User Provisioning | ${this.team?.company?.name || 'Company'} | RunDiffusion`,
		};
	},
	components: {
		EnterpriseUpsellBadge,
		BackButton,
		BaseBreadcrumbs,
		BaseButton,
		BaseToggle,
		ConfirmDialog,
		DoubleConfirmDangerDialog,
		DialogOrBottomSheet,
		DialogContent,
		EditCompanySsoConfigurations,
		ProvisioningControlBanner,
		ProvisioningStepCard,
		ScimConnectStep,
		ScimPlanStep,
		ScimDiffDialog,
	},
	mixins: [TeamUserMixin],
	data () {
		return {
			enforceAuthProvider: false,
			autoSyncEnabled: false,
			/** True once the Identity Provider owns membership. Set by deploying, not by a toggle. */
			scimProvisioningEnabled: false,
			/** SSO Exceptions (break-glass): normalized email -> true; exempt from enforcement. */
			emailExceptions: {} as Record<string, boolean>,
			saving: false,
			scimGroups: [] as ScimGroupRow[],
			activeCompanyTeams: [] as Team[],
			teamUserRoleDefs: [] as TeamUserRoleDef[],
			companyUserRoleDefs: [] as CompanyUserRoleDef[],
			loadingScimGroups: false,
			loadingCompanyTeams: false,
			/** The active-teams list is unusable (query failed, or was truncated by the cap). */
			companyTeamsLoadFailed: false,
			loadingTeamUserRoleDefs: false,
			loadingCompanyUserRoleDefs: false,
			savingPlan: false,
			scimGroupsUnsubscribe: null as null | (() => void),
			/** Snapshot of assignment fields per group when last loaded or saved; drives dirty and discard. */
			lastSavedScimGroups: [] as Array<{ id: string } & ScimAssignmentSnapshot>,
			/** Monotonic id for draft mapping rows; display order is derived, so v-for needs a stable key. */
			nextTeamMappingKey: 1,
			previewingScimSync: false,
			deploying: false,
			lastDryRun: null as DryRunRecord | null,
			diffDialogOpen: false,
			diffPayload: null as SyncResultPayload | null,
			diffRows: [] as DiffRow[],
			diffRanAt: 0,
			membersDialogOpen: false,
			membersDialogRow: null as ScimGroupRow | null,
			handOverDialogOpen: false,
			deployDialogOpen: false,
			handBackDialogOpen: false,
			openStages: {
				connect: false,
				plan: false,
				dryRun: false,
				deploy: false,
			},
			/** The flow auto-opens the stage an admin is on, but only once, so it never fights them. */
			stagesInitialized: false,
		};
	},
	computed: {
		...mapState([
			'team',
		]),
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		company (): Company | null {
			return this.team && typeof this.team.company !== 'string' ? this.team.company : null;
		},
		/**
		 * The same test that swaps the SSO section for its "not configured" empty state. Provisioning
		 * sits on top of SSO, so it stays hidden while that empty state is showing.
		 */
		ssoConfigured (): boolean {
			return !!this.companyClubOfferBenefits.isEnterprise && !!this.company?.sso?.identifier;
		},
		breadcrumbs () {
			return [
				{
					hoverColor: 'var(--primary-purple)',
					text: 'Company Dashboard',
					disabled: false,
					to: { name: ROUTER.COMPANY_DASHBOARD },
				},
				{
					hoverColor: 'var(--primary-blue)',
					text: 'Company Settings',
					disabled: false,
					to: { name: ROUTER.COMPANY_SETTINGS },
				},
				{
					hoverColor: 'var(--primary-blue)',
					text: 'SSO and User Provisioning',
					disabled: true,
					to: { name: ROUTER.COMPANY_SETTINGS_SSO_SCIM },
				},
			];
		},
		scimTokenLast4 (): string | null {
			return this.company?.sso?.scimAccessTokenLast4 ?? null;
		},
		hasScimToken (): boolean {
			return !!this.scimTokenLast4;
		},
		activeCompanyTeamsSorted (): Team[] {
			return sortTeamsByName(this.activeCompanyTeams);
		},
		/** Either run is in flight: the plan must not move underneath a run. */
		busy (): boolean {
			return this.previewingScimSync || this.deploying;
		},
		lastPushAt (): DbTimestamp | null {
			return this.scimGroups.reduce((latest: DbTimestamp | null, row: ScimGroupRow) => {
				const at = row.scimUpdatedAt ?? null;
				if (!at) return latest;
				if (!latest || at.toMillis() > latest.toMillis()) return at;
				return latest;
			}, null);
		},
		lastDeployAt (): DbTimestamp | null {
			return this.company?.sso?.lastScimSyncAt ?? null;
		},
		totalMemberEmails (): number {
			const emails = new Set<string>();
			this.scimGroups.forEach((row: ScimGroupRow) => {
				getMemberEmailsList(row).forEach((email: string) => emails.add(email));
			});
			return emails.size;
		},
		lastScimSyncStatus (): ScimSyncStatus | null {
			return this.company?.sso?.lastScimSyncStatus || null;
		},
		lastScimSyncCompletedWithErrors (): boolean {
			return this.lastScimSyncStatus === SCIM_SYNC_STATUS.COMPLETED_WITH_ERRORS;
		},
		/**
		 * The saved plan has been edited since the last deploy applied one, so that deploy no longer
		 * describes the current configuration. Distinct from "never deployed", which is `!lastDeployAt`.
		 */
		planChangedSinceLastDeploy (): boolean {
			const changedAt = this.company?.sso?.planChangedSinceLastScimSyncAt ?? null;
			if (!changedAt) return false;
			const deployedAt = this.lastDeployAt;
			if (!deployedAt) return true;
			return changedAt.toMillis() > deployedAt.toMillis();
		},
		/** The plan as last loaded or saved, keyed by group id. */
		savedSnapshotById (): Map<string, ScimAssignmentSnapshot> {
			return new Map<string, ScimAssignmentSnapshot>(
				this.lastSavedScimGroups.map(s => [s.id, {
					assignmentType: s.assignmentType,
					mappings: s.mappings,
					companyUserRoleDefId: s.companyUserRoleDefId,
				}]),
			);
		},
		planDirty (): boolean {
			if (this.lastSavedScimGroups.length !== this.scimGroups.length) return true;
			const byId = this.savedSnapshotById;
			return this.scimGroups.some((row: ScimGroupRow) => {
				const saved = byId.get(row.id);
				if (!saved) return true;
				return assignmentDiffersFromSnapshot(row, saved);
			});
		},
		hasMappedGroup (): boolean {
			return this.scimGroups.some((row: ScimGroupRow) => !isRowUnmapped(row));
		},
		planHasGaps (): boolean {
			// An unloadable team list counts as a gap: unknown is not the same as clean.
			return this.companyTeamsLoadFailed
				|| this.scimGroups.some((row: ScimGroupRow) => isRowUnmapped(row))
				|| this.companyRoleMembersWithoutTeam.length > 0
				|| teamsWithNoGroupInPlan(this.scimGroups, this.activeCompanyTeamsSorted).length > 0;
		},
		/**
		 * Members a company-scope group would give a role to while no group puts them in a team. Counts
		 * as a gap rather than blocking the deploy: whoever among them already has an account still gets
		 * their role, and the rest are reported as skipped by the sync. The page cannot tell the two
		 * apart, since it does not know which emails have accounts.
		 */
		companyRoleMembersWithoutTeam (): string[] {
			return companyRoleEmailsWithoutTeamMapping(this.scimGroups);
		},
		planStatusLabel (): string {
			if (!this.hasScimToken) return 'Not started';
			if (this.planDirty) return 'Unsaved';
			if (!this.hasMappedGroup) return 'Nothing mapped';
			if (this.planHasGaps) return 'Saved with gaps';
			return 'Saved';
		},
		/** Canonical form of the saved plan, so a dry run can tell whether it still describes reality. */
		planSignature (): string {
			return this.scimGroups
				.map((row: ScimGroupRow) => {
					const snapshot = getScimGroupAssignmentSnapshot(row);
					return `${row.id}:${snapshot.mappings}:${snapshot.companyUserRoleDefId ?? ''}`;
				})
				.sort()
				.join('|');
		},
		/**
		 * Identifies the pushed group state: newest scimUpdatedAt plus the sorted group ids. The id list
		 * matters because a group deleted in the IdP is hard-deleted here, so its removal never raises
		 * the timestamp.
		 */
		scimSignature (): string {
			const watermark = this.scimGroups.reduce((max: number, row: ScimGroupRow) => {
				const millis = row.scimUpdatedAt?.toMillis?.() ?? 0;
				return millis > max ? millis : max;
			}, 0);
			const ids = this.scimGroups.map((row: ScimGroupRow) => row.id).sort().join(',');
			return `${watermark}|${ids}`;
		},
		/** A dry run stops being quotable the moment the plan or the pushed groups move. */
		dryRunIsCurrent (): boolean {
			if (!this.lastDryRun?.success) return false;
			return this.lastDryRun.planSignature === this.planSignature
				&& this.lastDryRun.scimSignature === this.scimSignature;
		},
		canDryRun (): boolean {
			return this.hasMappedGroup && !this.planDirty && !this.busy;
		},
		/**
		 * The first deploy is the hand-over, so it requires a current dry run: an admin should never
		 * take manual editing away from their company without having seen the diff. Once the IdP is in
		 * control, deploying is routine and only needs a saved plan.
		 */
		canDeploy (): boolean {
			if (this.busy || this.planDirty || !this.hasMappedGroup) return false;
			if (this.scimProvisioningEnabled) return true;
			// Only the hand-over is blocked by an unknown team list. That deploy is the one that takes
			// manual editing away from a company's admins, so it must not proceed while the page cannot
			// name the teams it would empty. Once the Identity Provider is in control that membership is
			// already its to decide, and blocking every later deploy on a transient query failure would
			// strand an admin whose plan is the only thing standing between their groups and their teams.
			if (this.companyTeamsLoadFailed) return false;
			return this.dryRunIsCurrent;
		},
		deployBlockedReason (): string {
			if (this.busy) return '';
			if (!this.hasMappedGroup) return `Map at least one group to its ${this.displayLabels.team} and role first.`;
			if (this.planDirty) return 'Save your plan first.';
			if (this.companyTeamsLoadFailed && !this.scimProvisioningEnabled) {
				return `We could not load the full list of your ${pluralize(this.displayLabels.team)}, so we cannot show you which would be emptied by handing over control. Reload the page and try again.`;
			}
			if (!this.scimProvisioningEnabled && !this.dryRunIsCurrent) {
				return this.lastDryRun
					? 'Your plan or your groups changed since the last dry run. Run it again before handing over control.'
					: 'Run a dry run first so you can see exactly what handing over control would change.';
			}
			return '';
		},
		connectStage (): StageState {
			if (!this.hasScimToken) {
				return { status: 'Not connected', tone: 'active', subtitle: 'Generate a token and paste it into your Identity Provider.' };
			}
			if (!this.scimGroups.length) {
				return { status: 'Waiting for groups', tone: 'blocked', subtitle: 'Token generated. Waiting for your first group push.' };
			}
			return {
				status: 'Complete',
				tone: 'complete',
				subtitle: `SCIM endpoint and token · ${this.scimGroups.length} ${pluralize('group', this.scimGroups.length)} received`,
			};
		},
		planStage (): StageState {
			if (!this.hasScimToken || !this.scimGroups.length) return { status: 'Waiting', tone: 'idle' };
			if (this.planDirty) return { status: 'Draft, not live', tone: 'active' };
			if (!this.hasMappedGroup) return { status: 'Nothing mapped', tone: 'blocked' };
			// Amber, not green: the banner's Plan status already reads this same state in amber, and a
			// plan with unmapped groups is exactly the thing the stage list should still be pointing at.
			if (this.planHasGaps) return { status: 'Saved with gaps', tone: 'blocked' };
			return { status: 'Saved', tone: 'complete' };
		},
		dryRunStage (): StageState {
			if (this.dryRunIsCurrent) {
				return this.lastDryRun?.deployFailed
					? { status: 'Run again', tone: 'blocked' }
					: { status: 'Clean', tone: 'complete' };
			}
			if (this.lastDryRun) return { status: 'Out of date', tone: 'blocked' };
			if (this.canDryRun) return { status: 'Ready', tone: 'active' };
			return { status: 'Not run', tone: 'idle' };
		},
		/**
		 * Shown when the team list is unknown but the deploy is still allowed, which is every deploy
		 * after the hand-over. It does not block, so it must not read like the blocked message, but the
		 * admin should still know the destructive half of the plan is not being displayed.
		 */
		teamsUnknownAdvisory (): string {
			if (!this.companyTeamsLoadFailed || !this.scimProvisioningEnabled) return '';
			return `We could not load the full list of your ${pluralize(this.displayLabels.team)}, so the plan above is not showing which would be emptied. Reload the page if you want to see that before deploying.`;
		},
		deployStage (): StageState {
			if (this.scimProvisioningEnabled) {
				return {
					status: 'Live',
					tone: 'complete',
					subtitle: 'Your Identity Provider is the source of truth. Deploy again to apply plan changes.',
				};
			}
			if (this.canDeploy) {
				return {
					status: 'Ready',
					tone: 'active',
					subtitle: 'Applies the plan and makes your Identity Provider the source of truth.',
				};
			}
			// The hand-over is the blocked case, so the pill names the real obstacle rather than sending
			// the admin to a dry run that would not unblock anything.
			if (this.companyTeamsLoadFailed) {
				return {
					status: 'Unavailable',
					tone: 'blocked',
					subtitle: `We could not load your ${pluralize(this.displayLabels.team)}, so we cannot show what handing over control would empty.`,
				};
			}
			return {
				status: 'Dry run first',
				tone: 'idle',
				subtitle: 'Applies the plan and makes your Identity Provider the source of truth.',
			};
		},
		dryRunNote (): string {
			if (!this.hasMappedGroup) return `Map at least one group to its ${this.displayLabels.team} and role first.`;
			if (this.planDirty) return 'Save your plan to dry run it.';
			if (this.dryRunIsCurrent && this.lastDryRun) {
				if (this.lastDryRun.deployFailed) {
					return `Last dry run at ${moment(this.lastDryRun.at).format('h:mm a')}. A deploy of it failed part-way, so run it again for exact numbers.`;
				}
				return `Last dry run at ${moment(this.lastDryRun.at).format('h:mm a')}. Still current for this plan.`;
			}
			if (this.lastDryRun) return 'Your plan or your groups changed since the last dry run.';
			return 'A clean dry run unlocks the deploy below. Editing the plan clears the result.';
		},
		/**
		 * Why auto-deploy cannot be turned on yet, or '' when it can. Surfaced next to the toggle so the
		 * prerequisites are visible before the admin tries, rather than only as a snackbar afterwards.
		 */
		autoSyncDisabledReason (): string {
			if (!this.scimProvisioningEnabled) {
				return 'Deploy once to hand control to your Identity Provider before using auto-deploy.';
			}
			if (!this.hasMappedGroup) {
				return `Map at least one group to its ${this.displayLabels.team} and role first.`;
			}
			if (!this.lastDeployAt || this.lastScimSyncCompletedWithErrors) {
				return 'Run a deploy that completes without errors before turning on auto-deploy.';
			}
			if (this.planChangedSinceLastDeploy) {
				return 'Your plan changed since the last deploy. Deploy it once before turning on auto-deploy.';
			}
			return '';
		},
		handOverBodyText (): string {
			const teamLabel = this.displayLabels.team;
			const counts = this.lastDryRun
				? `Your dry run found ${this.lastDryRunCounts.added} to add and ${this.lastDryRunCounts.removed} to remove. `
					+ (this.lastDryRun.deployFailed
						? 'An earlier deploy of it failed part-way, so some of that may already be applied. '
						: '')
				: '';
			return `${counts}Deploying applies the plan to your ${pluralize(teamLabel)} and makes your Identity Provider the source of truth. `
				+ 'Removed members lose access immediately, and manual member and role editing turns off across RunDiffusion. '
				+ 'You can hand control back at any time, which leaves members where they are.';
		},
		/**
		 * Added and removed counts for the last dry run, taken from its flattened rows.
		 *
		 * Not `payload.addedTotal` / `payload.removedTotal`: those are team-membership counters. The
		 * reconcile builds them from `changesByTeam` alone and tracks company-role writes separately, so
		 * a company-role add or removal is missing from them while still appearing in the list, the
		 * chips, the tiles and the export. That is what made the confirm dialog read one lower than the
		 * "Removals" chip next to it. Counting the same rows the admin is looking at keeps every number
		 * on this page derived from one place.
		 */
		lastDryRunCounts (): { added: number; removed: number } {
			if (!this.lastDryRun) return { added: 0, removed: 0 };
			const counts = countByKind(this.lastDryRun.rows);
			return { added: counts.added, removed: counts.removed };
		},
		deployBodyText (): string {
			if (!this.lastDryRun || !this.dryRunIsCurrent) {
				return `This adds, removes and re-roles members across every ${this.displayLabels.team} in your plan, to match your groups. `
					+ 'Removed members lose access immediately. We recommend a dry run first so you can see exactly what will change.';
			}
			const { at, deployFailed } = this.lastDryRun;
			const { added, removed } = this.lastDryRunCounts;
			const ranAt = moment(at).isSame(moment(), 'day')
				? moment(at).format('h:mm a')
				: moment(at).format('MMM D [at] h:mm a');
			return `Your dry run at ${ranAt} found ${added} ${pluralize('member', added)} to add `
				+ `and ${removed} to remove. Removed members lose access immediately. `
				+ (deployFailed
					? 'A previous deploy of this plan failed part-way, so some of those changes may already have been applied and the real total may be smaller. Dry run again for exact numbers.'
					: 'Results can differ if your Identity Provider has pushed changes since that dry run.');
		},
		handBackBodyText (): string {
			return 'Your admins can edit members and roles by hand again, and auto-deploy turns off. '
				+ 'Nobody is removed and your plan is kept, so you can deploy again whenever you want.';
		},
		membersDialogTitle (): string {
			const row = this.membersDialogRow;
			return row ? (row.displayName || row.id) : 'Members';
		},
		membersDialogEmails (): string[] {
			return this.membersDialogRow ? getMemberEmailsList(this.membersDialogRow) : [];
		},
	},
	watch: {
		'team.company': {
			immediate: true,
			handler (company: Company) {
				if (company) {
					this.initLocalValues(company);
					this.loadActiveCompanyTeams(company.id);
					this.loadTeamUserRoleDefs(company.id);
					this.loadCompanyUserRoleDefs(company.id);
					this.bindScimGroups(company.id);
				} else {
					this.unbindScimGroups();
					this.scimGroups = [];
					this.lastSavedScimGroups = [];
					this.activeCompanyTeams = [];
					this.teamUserRoleDefs = [];
					this.companyUserRoleDefs = [];
				}
			},
		},
	},
	beforeDestroy () {
		this.unbindScimGroups();
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		goBack () {
			for (let i = this.breadcrumbs.length - 1; i >= 0; i--) {
				const b = this.breadcrumbs[i];
				if (b.to && !b.disabled) {
					this.$router.push(b.to);
					return;
				}
			}
		},
		initLocalValues (company: Company) {
			this.enforceAuthProvider = !!company?.sso?.enforceAuthProvider;
			this.autoSyncEnabled = !!company?.sso?.autoSyncEnabled;
			this.scimProvisioningEnabled = !!company?.sso?.scimProvisioningEnabled;
			this.emailExceptions = { ...(company?.sso?.emailExceptions || {}) };
		},
		/** Opens the stage the admin is actually on, once, after the first load settles. */
		initStages () {
			if (this.stagesInitialized) return;
			this.stagesInitialized = true;
			if (!this.hasScimToken || !this.scimGroups.length) {
				this.openStages.connect = true;
				return;
			}
			if (this.planDirty || !this.hasMappedGroup) {
				this.openStages.plan = true;
				return;
			}
			if (!this.scimProvisioningEnabled && !this.dryRunIsCurrent) {
				this.openStages.dryRun = true;
				return;
			}
			this.openStages.deploy = true;
		},
		onScimTokenCreated () {
			this.openStages.connect = true;
		},
		async onEnforceAuthProviderChange (val: boolean) {
			const previous = this.enforceAuthProvider;
			this.enforceAuthProvider = !!val;
			const saved = await this.saveSsoSettings({ 'sso.enforceAuthProvider': this.enforceAuthProvider });
			if (!saved) this.enforceAuthProvider = previous;
		},
		onAddEmailException (entry: string) {
			const key: string = normalizeSsoExceptionEntry(entry);
			if (!key) return;
			this.saveEmailExceptions({ ...this.emailExceptions, [key]: true });
		},
		onRemoveEmailException (entry: string) {
			const next: Record<string, boolean> = { ...this.emailExceptions };
			delete next[normalizeSsoExceptionEntry(entry)];
			this.saveEmailExceptions(next);
		},
		/**
		 * Whole-map write: the dotted path ends at emailExceptions, so the entry keys (which contain
		 * dots) are never parsed as field paths. Replacing the map also makes removals take effect.
		 * When the map is empty, delete the field entirely rather than persisting an empty {}.
		 */
		async saveEmailExceptions (next: Record<string, boolean>) {
			const previous = this.emailExceptions;
			this.emailExceptions = next;
			const saved = await this.saveSsoSettings({
				'sso.emailExceptions': _isEmpty(next) ? FieldValue.delete() : next,
			});
			// A failed write must not leave the page listing an exception Firestore never took: these
			// entries decide who may bypass SSO Enforcement.
			if (!saved) this.emailExceptions = previous;
		},
		async onAutoSyncEnabledChange (val: boolean) {
			const previous = this.autoSyncEnabled;
			this.autoSyncEnabled = !!val;
			// Re-check the same prerequisites the disabled toggle already explains, because they can go
			// stale between render and click. Quoting that reason keeps the two from drifting apart.
			// (Legacy syncs predate lastScimSyncStatus and have no status, which is treated as
			// acceptable, not errored.)
			if (!!val && !!this.autoSyncDisabledReason) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: this.autoSyncDisabledReason,
					show: true,
				});
				setTimeout(() => {
					// Let users see the toggle turn itself off
					this.autoSyncEnabled = false;
				}, 100);
				return;
			}
			const saved = await this.saveSsoSettings({ 'sso.autoSyncEnabled': this.autoSyncEnabled });
			// A failed write must not leave the page claiming auto-deploy is off while the cron still runs.
			if (!saved) this.autoSyncEnabled = previous;
		},
		/**
		 * Returns whether the write landed. Callers set their local flag optimistically so the control
		 * responds immediately, and must put it back on false: leaving the page claiming a setting that
		 * Firestore never took is worse than the control feeling slow, because both of these settings
		 * decide who is allowed to move members.
		 */
		async saveSsoSettings (updateData: Record<string, unknown>): Promise<boolean> {
			const company: Company | null = this.company;
			if (!company?.id || !company?.sso?.identifier) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Company or SSO not configured',
					show: true,
				});
				return false;
			}
			this.saving = true;
			try {
				await db.collection('companies').doc(company.id).update(updateData);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: 'Settings saved successfully',
					show: true,
				});
				return true;
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error saving. Please try again or contact support.',
					show: true,
				});
				return false;
			} finally {
				this.saving = false;
			}
		},
		async onConfirmHandBack (close?: () => void) {
			if (close) close();
			else this.handBackDialogOpen = false;
			const previousProvisioning = this.scimProvisioningEnabled;
			const previousAutoSync = this.autoSyncEnabled;
			this.scimProvisioningEnabled = false;
			this.autoSyncEnabled = false;
			const saved = await this.saveSsoSettings({
				'sso.scimProvisioningEnabled': false,
				'sso.autoSyncEnabled': false,
			});
			// Both flags go back on a failed write. Showing "Manual control" while Firestore still has the
			// Identity Provider in charge would invite hand edits that the next auto-deploy silently undoes.
			if (!saved) {
				this.scimProvisioningEnabled = previousProvisioning;
				this.autoSyncEnabled = previousAutoSync;
			}
		},
		mapScimGroupsSnapshotToRows (docs: Array<{ id: string; data: () => Record<string, unknown> }>): ScimGroupRow[] {
			return docs
				.filter(doc => (doc.data() as { isDeleted?: boolean }).isDeleted !== true)
				.map(doc => {
					const data = doc.data() as ScimGroupRow;
					const row: ScimGroupRow = { ...data, id: doc.id };
					// Stored discriminator when present, inferred from the populated field otherwise.
					row._assignmentType = (data.assignmentType === 'team' || data.assignmentType === 'company')
						? data.assignmentType
						: (data.companyUserRoleDefId ? 'company' : 'team');
					row._teamMappings = this.hydrateTeamMappings(data);
					return row;
				});
		},
		hydrateTeamMappings (data: ScimGroupRow): TeamMappingDraft[] {
			const stored = data.teamRoleMappings;
			if (!stored) return [];
			return Object.entries(stored).map(([teamId, teamUserRoleDefId]) => ({
				key: this.nextTeamMappingKey++,
				teamId: teamId || null,
				teamUserRoleDefId: teamUserRoleDefId || null,
			}));
		},
		hasUnsavedAssignmentForRow (row: ScimGroupRow, savedById: Map<string, ScimAssignmentSnapshot>): boolean {
			const saved = savedById.get(row.id);
			if (!saved) return false;
			return assignmentDiffersFromSnapshot(row, saved);
		},
		sortRowsByDisplayName (rows: ScimGroupRow[]): ScimGroupRow[] {
			// Client sort matches the Group column (displayName || id). A Firestore orderBy('displayName')
			// would drop docs without that field.
			return [...rows].sort((a, b) =>
				(a.displayName || a.id || '').localeCompare(b.displayName || b.id || '', undefined, { sensitivity: 'base' }),
			);
		},
		applyScimGroupsRows (rows: ScimGroupRow[], source: 'live' | 'load' = 'load') {
			const hadUnsavedChanges = this.planDirty;
			const previousScimSignature = this.scimSignature;
			const savedById = this.savedSnapshotById;
			const localById = new Map<string, ScimGroupRow>(this.scimGroups.map(r => [r.id, r]));
			// A row where our local draft wins over the document, and the document itself has moved since
			// we last read it, is somebody else's saved plan being masked. Only the assignment fields are
			// compared, because those are the only ones this page writes.
			let maskedAnotherEdit = false;
			const mergedRows = rows.map((incoming) => {
				const local = localById.get(incoming.id);
				if (!local) return incoming;
				if (!this.hasUnsavedAssignmentForRow(local, savedById)) return incoming;
				const saved = savedById.get(incoming.id);
				if (saved && assignmentDiffersFromSnapshot(incoming, saved)) maskedAnotherEdit = true;
				return {
					...incoming,
					companyUserRoleDefId: local.companyUserRoleDefId,
					_assignmentType: local._assignmentType ?? incoming._assignmentType,
					_teamMappings: local._teamMappings,
				};
			});
			this.scimGroups = this.sortRowsByDisplayName(mergedRows);
			this.lastSavedScimGroups = this.sortRowsByDisplayName(rows).map(r => ({
				id: r.id,
				...getScimGroupAssignmentSnapshot(r),
			}));
			// The signature moves only on an IdP push, because scimUpdatedAt and the id list are never
			// written from this page. That distinguishes a group push from a plan edit by a person.
			const isIdpUpdate = this.scimSignature !== previousScimSignature;
			// Our own save writes one doc at a time and refreshes lastSavedScimGroups only afterwards, so
			// its echoes arrive looking exactly like a concurrent edit. savingPlan rules them out.
			if (source === 'live' && maskedAnotherEdit && !this.savingPlan) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.WARN,
					message: 'Someone else changed this plan while you were editing. Your unsaved edits are still shown and will overwrite theirs if you save. Discard to see their version.',
					show: true,
				});
			} else if (source === 'live' && hadUnsavedChanges && isIdpUpdate) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.WARN,
					message: 'Your Identity Provider pushed new group data. Unsaved plan edits were preserved where possible, so please review before saving.',
					show: true,
				});
			}
			this.initStages();
		},
		unbindScimGroups () {
			if (this.scimGroupsUnsubscribe) {
				this.scimGroupsUnsubscribe();
				this.scimGroupsUnsubscribe = null;
			}
		},
		bindScimGroups (companyId: string | undefined) {
			this.unbindScimGroups();
			if (!companyId) {
				this.scimGroups = [];
				this.lastSavedScimGroups = [];
				return;
			}
			this.loadingScimGroups = true;
			this.scimGroupsUnsubscribe = db
				.collection('companies')
				.doc(companyId)
				.collection('scimGroups')
				.onSnapshot((snap) => {
					this.applyScimGroupsRows(this.mapScimGroupsSnapshotToRows(snap.docs), 'live');
					this.loadingScimGroups = false;
				}, (e) => {
					console.error(e);
					this.scimGroups = [];
					this.lastSavedScimGroups = [];
					this.loadingScimGroups = false;
				});
		},
		async loadScimGroups (companyId: string | undefined) {
			if (!companyId) {
				this.scimGroups = [];
				return;
			}
			this.loadingScimGroups = true;
			try {
				const snap = await db.collection('companies').doc(companyId).collection('scimGroups').get();
				this.applyScimGroupsRows(this.mapScimGroupsSnapshotToRows(snap.docs), 'load');
			} catch (e) {
				console.error(e);
				this.scimGroups = [];
				this.lastSavedScimGroups = [];
			} finally {
				this.loadingScimGroups = false;
			}
		},
		async loadActiveCompanyTeams (companyId: string | undefined) {
			if (!companyId) {
				this.activeCompanyTeams = [];
				this.companyTeamsLoadFailed = false;
				return;
			}
			this.loadingCompanyTeams = true;
			try {
				// One over the cap, so a company sitting exactly on it is not mistaken for a truncated
				// read. More than the cap means the list really is incomplete, which would understate
				// what a deploy empties.
				const snapshot = await db.collection('teams').where('companyId', '==', companyId).where('isActive', '==', true).limit(ACTIVE_TEAMS_QUERY_LIMIT + 1).get();
				this.companyTeamsLoadFailed = snapshot.docs.length > ACTIVE_TEAMS_QUERY_LIMIT;
				this.activeCompanyTeams = snapshot.docs
					.slice(0, ACTIVE_TEAMS_QUERY_LIMIT)
					.map(doc => ({ id: doc.id, ...doc.data() } as Team));
			} catch (e) {
				console.error(e);
				// An empty list reads as "no team is at risk", which is the most dangerous thing this page
				// can imply. Record the failure so the plan step says so and the deploy stays blocked.
				this.activeCompanyTeams = [];
				this.companyTeamsLoadFailed = true;
			} finally {
				this.loadingCompanyTeams = false;
			}
		},
		async loadTeamUserRoleDefs (companyId: string | undefined) {
			if (!companyId) {
				this.teamUserRoleDefs = [];
				return;
			}
			this.loadingTeamUserRoleDefs = true;
			try {
				const snapshot = await db.collection('teamUserRoleDefs').where('companyId', '==', companyId).where('isDeleted', '==', false).orderBy('level', 'desc').limit(100).get();
				this.teamUserRoleDefs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as TeamUserRoleDef));
			} catch (e) {
				console.error(e);
				this.teamUserRoleDefs = [];
			} finally {
				this.loadingTeamUserRoleDefs = false;
			}
		},
		async loadCompanyUserRoleDefs (companyId: string | undefined) {
			if (!companyId) {
				this.companyUserRoleDefs = [];
				return;
			}
			this.loadingCompanyUserRoleDefs = true;
			try {
				const snapshot = await db.collection('companyUserRoleDefs').where('companyId', '==', companyId).where('isDeleted', '==', false).orderBy('createdAt', 'desc').limit(200).get();
				this.companyUserRoleDefs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CompanyUserRoleDef));
			} catch (e) {
				console.error(e);
				this.companyUserRoleDefs = [];
			} finally {
				this.loadingCompanyUserRoleDefs = false;
			}
		},
		rowById (groupId: string): ScimGroupRow | undefined {
			return this.scimGroups.find((r: ScimGroupRow) => r.id === groupId);
		},
		onAssignmentTypeChange (groupId: string, assignmentType: string) {
			const row = this.rowById(groupId);
			if (!row) return;
			const type = assignmentType === 'company' ? 'company' : 'team';
			this.$set(row, '_assignmentType', type);
			// A group is either team-assigned or company-assigned, never both.
			if (type === 'company') {
				this.$set(row, '_teamMappings', []);
			} else {
				this.$set(row, 'companyUserRoleDefId', undefined);
				if (!(row._teamMappings ?? []).length) this.onAddTeamMapping(row.id);
			}
		},
		onAddTeamMapping (groupId: string) {
			const row = this.rowById(groupId);
			if (!row) return;
			this.$set(row, '_teamMappings', [...(row._teamMappings ?? []), {
				key: this.nextTeamMappingKey++,
				teamId: null,
				teamUserRoleDefId: null,
			}]);
		},
		onRemoveTeamMapping (groupId: string, key: number) {
			const row = this.rowById(groupId);
			if (!row) return;
			this.$set(row, '_teamMappings', (row._teamMappings ?? []).filter((m: TeamMappingDraft) => m.key !== key));
		},
		onTeamMappingTeamChange (groupId: string, key: number, teamId: string | null) {
			const row = this.rowById(groupId);
			if (!row) return;
			this.$set(row, '_teamMappings', (row._teamMappings ?? []).map((m: TeamMappingDraft) => (
				m.key === key ? { ...m, teamId: teamId ?? null } : m
			)));
		},
		onTeamMappingRoleChange (groupId: string, key: number, teamUserRoleDefId: string | null) {
			const row = this.rowById(groupId);
			if (!row) return;
			this.$set(row, '_teamMappings', (row._teamMappings ?? []).map((m: TeamMappingDraft) => (
				m.key === key ? { ...m, teamUserRoleDefId: teamUserRoleDefId ?? null } : m
			)));
		},
		onCompanyRoleChange (groupId: string, companyUserRoleDefId: string | null) {
			const row = this.rowById(groupId);
			if (row) this.$set(row, 'companyUserRoleDefId', companyUserRoleDefId ?? undefined);
		},
		onDiscardPlan () {
			const byId = this.savedSnapshotById;
			this.scimGroups.forEach((row: ScimGroupRow) => {
				const saved = byId.get(row.id);
				if (!saved) return;
				this.$set(row, 'companyUserRoleDefId', saved.companyUserRoleDefId ?? undefined);
				// Restore the saved discriminator rather than inferring it: a company-scope group with no
				// role yet snapshots to a null role, which would otherwise read back as team scope.
				this.$set(row, '_assignmentType', saved.assignmentType);
				// Rebuild drafts from the saved canonical string so discarded half-filled entries do not survive.
				this.$set(row, '_teamMappings', teamMappingsFromSnapshot(saved, () => this.nextTeamMappingKey++));
			});
		},
		async onSavePlan () {
			const company: Company | null = this.company;
			if (!company?.id) return;
			const byId = this.savedSnapshotById;
			const toUpdate = this.scimGroups.filter((row: ScimGroupRow) => {
				const saved = byId.get(row.id);
				if (!saved) return true;
				return assignmentDiffersFromSnapshot(row, saved);
			});
			if (toUpdate.length === 0) return;
			this.savingPlan = true;
			// Each success is recorded as it lands, so a failure part-way through leaves an accurate
			// picture instead of one where every write looks unsaved. Without this, the rows that did
			// save still read as dirty, and their snapshot echoes arrive looking like another admin's
			// edit, because the document has moved but our record of it has not.
			const savedById = new Map<string, { id: string } & ScimAssignmentSnapshot>(
				this.lastSavedScimGroups.map(s => [s.id, s]),
			);
			try {
				const companyRef = db.collection('companies').doc(company.id);
				const scimGroupsRef = companyRef.collection('scimGroups');
				for (const row of toUpdate) {
					const isCompanyAssignment = getRowAssignmentType(row) === 'company';
					// State the kind of assignment, and delete the side that is not in use rather than
					// nulling it, so "not set" has a single representation on the document.
					await scimGroupsRef.doc(row.id).update(
						isCompanyAssignment
							? {
								assignmentType: 'company',
								companyUserRoleDefId: row.companyUserRoleDefId ?? null,
								teamRoleMappings: FieldValue.delete(),
							}
							: {
								assignmentType: 'team',
								teamRoleMappings: teamRoleMappingsForRow(row),
								companyUserRoleDefId: FieldValue.delete(),
							},
					);
					savedById.set(row.id, { id: row.id, ...getScimGroupAssignmentSnapshot(row) });
				}
				// A new plan means the last deploy no longer describes the current configuration, and
				// auto-deploy must be re-armed deliberately after the admin has reviewed the change.
				// Recorded as its own marker rather than by nulling lastScimSyncAt: that timestamp is the
				// only record of whether the Identity Provider has ever actually applied anything, and
				// erasing it made an established tenant read as "Last deploy: None yet".
				const planChangedUpdate: Record<string, unknown> = {
					'sso.planChangedSinceLastScimSyncAt': FieldValue.serverTimestamp(),
				};
				const pausesAutoSync = this.autoSyncEnabled;
				if (pausesAutoSync) planChangedUpdate['sso.autoSyncEnabled'] = false;
				await companyRef.update(planChangedUpdate);
				// Only after the write, so a failure cannot leave the page showing auto-deploy as paused
				// while Firestore still has it on and the cron keeps deploying the old plan.
				if (pausesAutoSync) this.autoSyncEnabled = false;
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Plan saved. ${toUpdate.length} ${pluralize('group', toUpdate.length)} updated.`,
					show: true,
				});
				this.openStages.dryRun = true;
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to save. Try again.',
					show: true,
				});
			} finally {
				// Rows that saved are recorded, rows that did not are left out. A missing row makes the
				// lengths differ, which planDirty already reads as unsaved, so a retry rewrites only what
				// still needs it.
				this.lastSavedScimGroups = this.scimGroups
					.map((r: ScimGroupRow) => savedById.get(r.id))
					.filter((s): s is { id: string } & ScimAssignmentSnapshot => !!s);
				this.savingPlan = false;
			}
		},
		teamRoleNameById (roleDefId: string): string {
			const rd = this.teamUserRoleDefs.find((r: TeamUserRoleDef) => r.id === roleDefId);
			const n = typeof rd?.name === 'string' ? rd.name.trim() : '';
			return n.length > 0 ? n : 'name unknown';
		},
		companyRoleNameById (roleDefId: string): string {
			const rd = this.companyUserRoleDefs.find((r: CompanyUserRoleDef) => r.id === roleDefId);
			const n = typeof rd?.name === 'string' ? rd.name.trim() : '';
			return n.length > 0 ? n : 'name unknown';
		},
		buildDiffRows (payload: SyncResultPayload): DiffRow[] {
			return flattenSyncResult(
				payload,
				this.teamRoleNameById,
				this.companyRoleNameById,
				`Company (${this.company?.name || 'company'})`,
			);
		},
		onDeployClick () {
			if (!this.canDeploy) return;
			if (this.scimProvisioningEnabled) this.deployDialogOpen = true;
			else this.handOverDialogOpen = true;
		},
		onConfirmDeploy (close?: () => void) {
			if (close) close();
			else this.deployDialogOpen = false;
			this.onRunSync(false);
		},
		onConfirmHandOverDeploy (reset?: () => void) {
			this.handOverDialogOpen = false;
			if (reset) reset();
			this.onRunSync(false);
		},
		reopenLastDryRun () {
			if (!this.lastDryRun) return;
			this.diffPayload = this.lastDryRun.payload;
			this.diffRows = this.lastDryRun.rows;
			this.diffRanAt = this.lastDryRun.at;
			this.diffDialogOpen = true;
		},
		async onRunSync (dryRun: boolean) {
			const company: Company | null = this.company;
			if (!company?.id) return;
			// Sampled before the call so a push arriving mid-run is not baked into the record.
			const scimSignatureAtStart = this.scimSignature;
			const planSignatureAtStart = this.planSignature;
			const isHandOver = !dryRun && !this.scimProvisioningEnabled;
			if (dryRun) this.previewingScimSync = true;
			else this.deploying = true;
			try {
				const fn = functions.httpsCallable('syncTeamMembersFromScimGroups');
				const result = await fn({
					companyId: company.id,
					dryRun,
					...(isHandOver ? { handOverControl: true } : {}),
				});
				const data = result?.data as SyncResultPayload & { success?: boolean; message?: string };
				const hasDialogPayload = !!(
					data?.changesByTeam
					|| data?.changesByCompanyRole
					|| (data?.warnings && data.warnings.length > 0)
					|| (data?.errors && data.errors.length > 0)
				);

				if (!hasDialogPayload) {
					if (!data?.success) {
						// Leaving the previous dry run on screen behind the snackbar reads as if this run
						// produced it. There is nothing to show for this one, so show nothing.
						this.diffDialogOpen = false;
						this.updateSnackbar({
							status: SNACKBAR_STATUS.ERROR,
							message: data?.message || (dryRun ? 'Dry run failed' : 'Deploy failed'),
							show: true,
						});
					}
					return;
				}

				const payload: SyncResultPayload = {
					dryRun: !!data.dryRun,
					addedTotal: data.addedTotal,
					removedTotal: data.removedTotal,
					changesByTeam: data.changesByTeam || [],
					changesByCompanyRole: data.changesByCompanyRole,
					warnings: data.warnings || [],
					errors: data.errors || [],
				};
				const rows = this.buildDiffRows(payload);
				const at = Date.now();
				this.diffPayload = payload;
				this.diffRows = rows;
				this.diffRanAt = at;
				this.diffDialogOpen = true;

				if (dryRun) {
					// A blocked dry run comes back with errors and no totals, which would record as a
					// "0 changes" all-clear and be quoted back before a destructive deploy.
					this.lastDryRun = {
						at,
						payload,
						rows,
						scimSignature: scimSignatureAtStart,
						planSignature: planSignatureAtStart,
						success: !!data?.success,
					};
					if (data?.success) this.openStages.deploy = true;
					return;
				}

				if (data?.success) {
					// A clean deploy consumed the dry run; the next confirmation must not quote stale numbers.
					this.lastDryRun = null;
					if (isHandOver) this.scimProvisioningEnabled = true;
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Deployed. ${data.addedTotal ?? 0} added, ${data.removedTotal ?? 0} removed.`,
						show: true,
					});
					await this.loadScimGroups(company.id);
				} else {
					// Keep the dry run so a retry does not require re-running step 03, but mark it: the
					// deploy may have written some of it before failing, so its counts are now indicative.
					if (this.lastDryRun) this.lastDryRun = { ...this.lastDryRun, deployFailed: true };
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: data?.message || 'Deploy completed with errors',
						show: true,
					});
					// Members may have moved even though the run reported errors, so refresh what we show.
					await this.loadScimGroups(company.id);
				}
			} catch (e) {
				console.error(e);
				// Same reason as the no-payload branch: a stale diff must not stand in for this run.
				this.diffDialogOpen = false;
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: (e as { message?: string })?.message || 'Failed to sync team members',
					show: true,
				});
			} finally {
				if (dryRun) this.previewingScimSync = false;
				else this.deploying = false;
			}
		},
		/** `rows` is what the dialog is currently showing, so the file matches the review on screen. */
		onExportCsv (rows?: DiffRow[]) {
			if (!this.diffPayload) return;
			// Filenames distinguish the modes so an exported dry run cannot be mistaken for what ran.
			const prefix = this.diffPayload.dryRun ? 'scim-dry-run' : 'scim-deploy-results';
			const stamp = moment().format('YYYY-MM-DD-HHmm');
			initiateFileDownload(buildSyncResultCsv(rows ?? this.diffRows), `${prefix}-${stamp}.csv`);
		},
		openMembersDialog (row: ScimGroupRow) {
			this.membersDialogRow = row;
			this.membersDialogOpen = true;
		},
		closeMembersDialog () {
			this.membersDialogOpen = false;
			this.membersDialogRow = null;
		},
	},
});
